import {
  groupMemoryKey, isGroupOpen, latestGroupMemory, parseGroupMemory, readGroupMemory, toggleGroup, writeGroupMemory,
  type GroupMemory,
} from './menuMemory';

/**
 * The menu's open and closed groups for the whole app, one entry per user (Warren, 28 October 2026). The desktop bar
 * and the phone drawer are both mounted at once and read this one store through hooks/useMenuGroups.ts, so they
 * always agree: on what each group was left as (remembered in localStorage, lib/menuMemory.ts) and on the groups
 * closed by a click while on the page inside them.
 *
 * Several tabs share one localStorage entry per user, so a click re-reads it before flipping its group (a click in one
 * tab never erases another tab's groups), and a change made in another tab arrives as a storage event (storageChanged).
 * Without a user id nothing is stored; a click still works for this visit. Pure apart from the storage it is given.
 */
export interface MenuGroupState {
  /** Each group's open (true) or closed (false) state, by menuGroupKey, as last left. */
  readonly memory: GroupMemory;
  /** Groups closed by a click while on the page inside them, and that page. Any other page has none. */
  readonly closedHere: { readonly path: string; readonly keys: ReadonlySet<string> };
}

type GroupStorage = Pick<Storage, 'getItem' | 'setItem'> | null;

const NOTHING_CLOSED: MenuGroupState['closedHere'] = { path: '', keys: new Set() };

/** Whether a group is open for this state on this page (lib/menuMemory.ts isGroupOpen). */
export function groupOpenIn(state: MenuGroupState, key: string, holdsCurrentPage: boolean, currentPath: string): boolean {
  const closedOnThisPage = state.closedHere.path === currentPath && state.closedHere.keys.has(key);
  return isGroupOpen({ remembered: state.memory[key], holdsCurrentPage, closedOnThisPage });
}

export function createMenuGroupStore(storage: () => GroupStorage) {
  const states = new Map<string, MenuGroupState>();
  const listeners = new Set<() => void>();
  const notify = () => listeners.forEach((l) => l());

  /** This user's state; the same object until something changes, as useSyncExternalStore needs. */
  function get(userId: string): MenuGroupState {
    let state = states.get(userId);
    if (!state) {
      state = { memory: readGroupMemory(userId || null, storage()), closedHere: NOTHING_CLOSED };
      states.set(userId, state);
    }
    return state;
  }

  /** A click on one group: it flips, every other group stays as this user last left it, in any tab. */
  function toggle(userId: string, key: string, holdsCurrentPage: boolean, currentPath: string): void {
    const state = get(userId);
    const wasOpen = groupOpenIn(state, key, holdsCurrentPage, currentPath);
    const memory = toggleGroup(latestGroupMemory(userId || null, state.memory, storage()), key, wasOpen);
    writeGroupMemory(userId || null, memory, storage());
    const keys = new Set(state.closedHere.path === currentPath ? state.closedHere.keys : []);
    if (wasOpen && holdsCurrentPage) keys.add(key); else keys.delete(key);
    states.set(userId, { memory, closedHere: { path: currentPath, keys } });
    notify();
  }

  /** A new page forgets the groups closed on the last one, so the next page inside a group opens it again. */
  function visit(userId: string, currentPath: string): void {
    const state = get(userId);
    if (state.closedHere.path === currentPath || state.closedHere.keys.size === 0) return;
    states.set(userId, { ...state, closedHere: NOTHING_CLOSED });
    notify();
  }

  /** Another tab wrote localStorage: a key of null is a clear, which forgets every user's groups. */
  function storageChanged(key: string | null, newValue: string | null): void {
    let changed = false;
    for (const [userId, state] of states) {
      const own = groupMemoryKey(userId);
      if (!own || (key !== null && key !== own)) continue;
      states.set(userId, { ...state, memory: parseGroupMemory(key === null ? null : newValue) });
      changed = true;
    }
    if (changed) notify();
  }

  function subscribe(listener: () => void): () => void {
    listeners.add(listener);
    return () => { listeners.delete(listener); };
  }

  return { get, toggle, visit, storageChanged, subscribe };
}

export type MenuGroupStore = ReturnType<typeof createMenuGroupStore>;
